import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { getAdsByRegion, getApiError } from '../../../utils/api';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CategoryIcon from '@mui/icons-material/Category';
import ReceiptIcon from '@mui/icons-material/Receipt';

// Helper functions for filtering
const ciMatch = (a, b) => (a || '').toString().toLowerCase() === (b || '').toString().toLowerCase();
const ciIncludes = (a, b) => (a || '').toString().toLowerCase().includes((b || '').toString().toLowerCase());

function AdAnalytics() {
  const [allAds, setAllAds] = useState([]); // Store all loaded ads for filtering
  const [filteredAds, setFilteredAds] = useState([]); // Store filtered ads for display
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [allTargetPincodes, setAllTargetPincodes] = useState([]);
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [filters, setFilters] = useState({ 
    targetPincode: '', 
    businessName: '', 
    category: ''
  });
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryBusiness, setGalleryBusiness] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    total_pages: 0,
    current_page: 1,
    page_size: 20,
    hasMore: false
  });

  useEffect(() => {
    fetchAds(1, true); 
  }, []);

  useEffect(() => {
    updateFilterOptions();
    applyFilters();
  }, [allAds]);

  useEffect(() => {
    applyFilters();
  }, [filters]);

  const fetchAds = async (page = 1, reset = false) => {
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError('');
    
    try {
      const res = await getAdsByRegion({ page: page });
      console.log(`API Response for page ${page}:`, res);
      
      const responseData = res.data || {};
      
      if (responseData.results && Array.isArray(responseData.results)) {
        const newAds = responseData.results;
        
        setAllAds(prevAds => reset ? newAds : [...prevAds, ...newAds]);
        
        setPagination({
          count: responseData.count || 0,
          total_pages: responseData.total_pages || 0,
          current_page: responseData.current_page || page,
          page_size: responseData.page_size || 20,
          hasMore: responseData.next !== null
        });
      } else if (Array.isArray(responseData)) {
        const newAds = responseData;
        
        setAllAds(prevAds => reset ? newAds : [...prevAds, ...newAds]);
        
        setPagination({
          count: newAds.length,
          total_pages: 1,
          current_page: 1,
          page_size: newAds.length,
          hasMore: false
        });
      } else {
        console.warn('Unexpected API response format:', responseData);
        setAllAds([]);
        setPagination({
          count: 0,
          total_pages: 0,
          current_page: 1,
          page_size: 20,
          hasMore: false
        });
      }
      
    } catch (err) {
      console.error(`Error fetching ads for page ${page}:`, err);
      setError(getApiError(err) || 'Failed to fetch advertisements');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = pagination.current_page + 1;
    fetchAds(nextPage, false);
  };

  const updateFilterOptions = () => {
    const targetPincodes = new Set();
    const businessNames = new Set();
    const categories = new Set();
    
    allAds.forEach(ad => {
      if (ad.businessName) businessNames.add(ad.businessName);
      if (ad.category) categories.add(ad.category);
      
      (ad.ads || []).forEach(adItem => {
        (adItem.target_pincodes || []).forEach(pincode => {
          if (pincode) targetPincodes.add(pincode);
        });
      });
    });
    
    setAllTargetPincodes(Array.from(targetPincodes).sort());
    setAllBusinesses(Array.from(businessNames).sort());
    setAllCategories(Array.from(categories).sort());
  };

  const applyFilters = () => {
    let filtered = allAds;
    
    if (filters.targetPincode) {
      filtered = filtered.filter(ad => 
        (ad.ads || []).some(adItem => 
          (adItem.target_pincodes || []).some(pincode => 
            ciMatch(pincode, filters.targetPincode)
          )
        )
      );
    }
    
    if (filters.businessName) {
      filtered = filtered.filter(ad => ciIncludes(ad.businessName, filters.businessName));
    }

    if (filters.category) {
      filtered = filtered.filter(ad => ciMatch(ad.category, filters.category));
    }
    
    setFilteredAds(filtered);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({ targetPincode: '', businessName: '', category: '' });
  };

  // Remove a single filter
  const handleRemoveFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: '' }));
  };

  // Open gallery modal for a business
  const handleOpenGallery = (business) => {
    setGalleryBusiness(business);
    setGalleryOpen(true);
  };

  const handleCloseGallery = () => {
    setGalleryOpen(false);
    setGalleryBusiness(null);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success.main';
      case 'upcoming': return 'warning.main';
      case 'expired': return 'error.main';
      default: return 'text.secondary';
    }
  };

  const getStatusChipColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'upcoming': return 'warning';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const getBusinessTargetPincodes = (business) => {
    const targetPincodes = new Set();
    (business.ads || []).forEach(ad => {
      (ad.target_pincodes || []).forEach(pincode => {
        if (pincode) targetPincodes.add(pincode);
      });
    });
    return Array.from(targetPincodes);
  };

  const getAdSummary = () => {
    let totalActive = 0;
    let totalExpired = 0;
    let totalUpcoming = 0;

    filteredAds.forEach(business => {
      (business.ads || []).forEach(ad => {
        switch (ad.status?.toLowerCase()) {
          case 'active':
            totalActive++;
            break;
          case 'expired':
            totalExpired++;
            break;
          case 'upcoming':
            totalUpcoming++;
            break;
        }
      });
    });

    return { totalActive, totalExpired, totalUpcoming };
  };

  const { totalActive, totalExpired, totalUpcoming } = getAdSummary();

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 4, px: { xs: 1, md: 0 } }}>
      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" fontWeight={700} color="text.primary" align="center" mb={3}>
          Ad Analytics by Region
        </Typography>

        <Grid container spacing={2} mb={3} justifyContent="center">
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {totalActive}
              </Typography>
              <Typography variant="body2" color="success.dark">
                Active Ads
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {totalUpcoming}
              </Typography>
              <Typography variant="body2" color="warning.dark">
                Upcoming Ads
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
              <Typography variant="h4" fontWeight={700} color="error.main">
                {totalExpired}
              </Typography>
              <Typography variant="body2" color="error.dark">
                Expired Ads
              </Typography>
            </Card>
          </Grid>
        </Grid>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2} alignItems={{ md: 'center' }}>
          <Autocomplete
            options={allTargetPincodes}
            value={filters.targetPincode}
            onChange={(_, value) => handleFilterChange('targetPincode', value || '')}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Pincode" 
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <LocationOnIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
            )}
            sx={{ minWidth: 180, flex: 1 }}
            clearOnEscape
            autoHighlight
            disableClearable={false}
          />
          
          <Autocomplete
            options={allCategories}
            value={filters.category}
            onChange={(_, value) => handleFilterChange('category', value || '')}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Category" 
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <CategoryIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
            )}
            sx={{ minWidth: 180, flex: 1 }}
            clearOnEscape
            autoHighlight
            disableClearable={false}
          />
          
          <Autocomplete
            options={allBusinesses}
            value={filters.businessName}
            onChange={(_, value) => handleFilterChange('businessName', value || '')}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Business Name" 
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <BusinessIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
            )}
            sx={{ minWidth: 220, flex: 2 }}
            clearOnEscape
            autoHighlight
            disableClearable={false}
          />
          
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={handleClearFilters} 
            startIcon={<FilterListIcon />}
            sx={{ height: 40, minWidth: 120 }}
          >
            Clear Filters
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" gap={1}>
          {filters.targetPincode && (
            <Chip 
              label={`Pincode: ${filters.targetPincode}`} 
              onDelete={() => handleRemoveFilter('targetPincode')} 
              color="primary" 
              size="small"
            />
          )}
          {filters.category && (
            <Chip 
              label={`Category: ${filters.category}`} 
              onDelete={() => handleRemoveFilter('category')} 
              color="primary" 
              size="small"
            />
          )}
          {filters.businessName && (
            <Chip 
              label={`Business: ${filters.businessName}`} 
              onDelete={() => handleRemoveFilter('businessName')} 
              color="primary" 
              size="small"
            />
          )}
        </Stack>

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && (
          <>
            <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>
                      Business Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>
                      Category
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>
                      Pincodes
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAds.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary" fontWeight={500} py={3}>
                          {allAds.length === 0 ? 'No advertisements found.' : 'No advertisements found for the selected filters.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAds.map((ad, idx) => {
                      const activeAds = (ad.ads || []).filter(a => a.status === 'Active');
                      const upcomingAds = (ad.ads || []).filter(a => a.status === 'Upcoming');
                      const expiredAds = (ad.ads || []).filter(a => a.status === 'Expired');
                      const hasImages = (ad.ads || []).some(a => a.image_url);
                      const targetPincodes = getBusinessTargetPincodes(ad);
                      
                      return (
                        <TableRow key={`${ad.businessName}-${ad.pincode}-${idx}`} hover>
                          <TableCell align="left" sx={{ verticalAlign: 'middle' }}>
                            <Typography fontWeight={600} color="text.primary">
                              {ad.businessName}
                            </Typography>
                          </TableCell>
                          <TableCell align="left" sx={{ verticalAlign: 'middle' }}>
                            <Typography variant="body2" color="text.secondary">
                              {ad.category}
                            </Typography>
                          </TableCell>
                          <TableCell align="left" sx={{ verticalAlign: 'middle' }}>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                              {targetPincodes.length > 0 ? (
                                targetPincodes.slice(0, 3).map((pincode, i) => (
                                  <Chip 
                                    key={i} 
                                    label={pincode} 
                                    size="small" 
                                    variant="outlined"
                                    color="primary"
                                  />
                                ))
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No target pincodes
                                </Typography>
                              )}
                              {targetPincodes.length > 3 && (
                                <Chip 
                                  label={`+${targetPincodes.length - 3} more`} 
                                  size="small" 
                                  variant="outlined"
                                  color="default"
                                />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="left" sx={{ verticalAlign: 'middle' }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              {activeAds.length > 0 && (
                                <Chip 
                                  label={`${activeAds.length} Active`} 
                                  size="small" 
                                  color="success" 
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                              {upcomingAds.length > 0 && (
                                <Chip 
                                  label={`${upcomingAds.length} Upcoming`} 
                                  size="small" 
                                  color="warning" 
                                  variant="outlined"
                                />
                              )}
                              {expiredAds.length > 0 && (
                                <Chip 
                                  label={`${expiredAds.length} Expired`} 
                                  size="small" 
                                  color="error" 
                                  variant="outlined"
                                />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="left" sx={{ verticalAlign: 'middle' }}>
                            {hasImages && (
                              <Tooltip title="View advertisement images" arrow>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  startIcon={<ImageIcon />}
                                  sx={{ 
                                    fontWeight: 600, 
                                    borderRadius: 2, 
                                    textTransform: 'none', 
                                    px: 2, 
                                    py: 0.5, 
                                    fontSize: 12,
                                    boxShadow: 2,
                                    '&:hover': {
                                      boxShadow: 4,
                                      transform: 'translateY(-1px)'
                                    }
                                  }}
                                  onClick={() => handleOpenGallery(ad)}
                                >
                                  View Gallery
                                </Button>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {pagination.hasMore && !loadingMore && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleLoadMore}
                  startIcon={<ExpandMoreIcon />}
                  sx={{
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'none'
                  }}
                >
                  Load More
                </Button>
              </Box>
            )}

            {loadingMore && (
              <Box display="flex" justifyContent="center" alignItems="center" mt={3} gap={2}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading more...
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Gallery Dialog */}
      <Dialog open={galleryOpen} onClose={handleCloseGallery} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {galleryBusiness && (
            <>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {galleryBusiness.businessName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Category: {galleryBusiness.category}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseGallery} size="small">
                <CloseIcon />
              </IconButton>
            </>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {galleryBusiness && (galleryBusiness.ads || []).filter(a => a.image_url).length === 0 && (
            <Typography color="text.secondary" align="center" py={3}>
              No images for this business.
            </Typography>
          )}
          <Grid container spacing={3}>
            {galleryBusiness && (galleryBusiness.ads || []).filter(a => a.image_url).map((img, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: 3, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  border: `2px solid`,
                  borderColor: getStatusColor(img.status)
                }}>
                  <CardMedia
                    component="img"
                    image={img.image_url}
                    alt={galleryBusiness.businessName}
                    sx={{ 
                      height: 200, 
                      objectFit: 'cover', 
                      borderTopLeftRadius: 12, 
                      borderTopRightRadius: 12 
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack spacing={2}>
                      <Chip 
                        label={img.status} 
                        color={getStatusChipColor(img.status)} 
                        size="small" 
                        sx={{ alignSelf: 'flex-start' }}
                      />
                      
                      <Typography variant="body2" color="text.secondary">
                        <strong>Duration:</strong><br />
                        {new Date(img.start_time).toLocaleDateString()} - {new Date(img.end_time).toLocaleDateString()}
                      </Typography>

                      {/* Max Invoice Info */}
                      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <ReceiptIcon color="primary" />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              Invoice Limit: {img.max_invoices}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Used: {img.invoice_count} / {img.max_invoices}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>

                      {img.target_pincodes && img.target_pincodes.length > 0 && (
                        <Box>
                          <Typography variant="body2" fontWeight={600} mb={1}>
                            Target Pincodes:
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                            {img.target_pincodes.map((pincode, i) => (
                              <Chip 
                                key={i} 
                                label={pincode} 
                                size="small" 
                                variant="outlined" 
                                color="primary"
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default AdAnalytics;